import { create, StateCreator } from "zustand";
import { createJSONStorage, persist, PersistOptions } from "zustand/middleware";

import { FetchedFolder } from "@/services/api/fetchers/folders";
import { FetchedNote } from "@/services/api/fetchers/notes";
import { FetchedTag } from "@/services/api/fetchers/tags";
import { TodoRow } from "@/services/api/fetchers/todos";
import { DailyStatsRes } from "@/services/api/fetchers/daily-user-stats";
import { indexDBStorage } from "../../shared/utils/indexdb";
import createUserSlice, { UserSlice } from "./userSlice";
import createDeviceSlice, { DeviceSlice } from "./deviceSlice";
import createFoldersSlice, { FoldersSlice } from "./foldersSlice";
import createNotesSlice, { NotesSlice } from "./notesSlice";
import createTagsSlice, { TagsSlice } from "./tagsSlice";
import createTodosSlice, { TodosSlice } from "./todosSlice";
import createNavigationSlice, { NavigationSlice } from "./navigationSlice";
import createSettingsSlice, { SettingsSlice } from "./settingsSlice";
import createDailyStatsSlice, { DailyStatsSlice } from "./dailyStatsSlice";
import createPageLayoutSlice, { PageLayoutSlice } from "./pageLayoutSlice";
import createEditorSlice, { EditorSlice } from "./editorSlice";

export type BoundStore = DailyStatsSlice &
  DeviceSlice &
  EditorSlice &
  FoldersSlice &
  NavigationSlice &
  NotesSlice &
  PageLayoutSlice &
  SettingsSlice &
  TagsSlice &
  TodosSlice &
  UserSlice;

interface PersistedState {
  dailyStats: {
    today: DailyStatsRes | null;
    yesterday: DailyStatsRes | null;
  };
  folders: {
    main: FetchedFolder[];
  };
  notes: {
    main: FetchedNote[];
    deleted: FetchedNote[];
  };
  tags: {
    list: FetchedTag[];
  };
  todos: {
    items: TodoRow[];
    completed: TodoRow[];
  };
}

// Create a version of store without persistence first to use for initialization
const createBoundStore = (...a: Parameters<StateCreator<BoundStore>>) => ({
  ...createDailyStatsSlice(...a),
  ...createDeviceSlice(...a),
  ...createEditorSlice(...a),
  ...createFoldersSlice(...a),
  ...createNavigationSlice(...a),
  ...createNotesSlice(...a),
  ...createPageLayoutSlice(...a),
  ...createSettingsSlice(...a),
  ...createTagsSlice(...a),
  ...createTodosSlice(...a),
  ...createUserSlice(...a),
});

const persistConfig: PersistOptions<BoundStore, PersistedState> = {
  name: "ink-rows-storage",
  storage: createJSONStorage(() => indexDBStorage),

  partialize: (state) => ({
    dailyStats: {
      today: state.dailyStats.today,
      yesterday: state.dailyStats.yesterday,
    },
    folders: {
      main: state.folders.main,
    },
    notes: {
      main: state.notes.main,
      deleted: state.notes.deleted,
    },
    tags: {
      list: state.tags.list,
    },
    todos: {
      items: state.todos.items,
      completed: state.todos.completed,
    },
  }),

  merge: (persistedState, currentState) => {
    const typedPersistedState = persistedState as PersistedState;
    return {
      ...currentState,
      dailyStats: {
        ...currentState.dailyStats,
        today: typedPersistedState.dailyStats?.today || null,
        yesterday: typedPersistedState.dailyStats?.yesterday || null,
      },
      folders: {
        ...currentState.folders,
        main: typedPersistedState.folders?.main || [],
      },
      notes: {
        ...currentState.notes,
        main: typedPersistedState.notes?.main || [],
        deleted: typedPersistedState.notes?.deleted || [],
      },
      tags: {
        ...currentState.tags,
        list: typedPersistedState.tags?.list || [],
      },
      todos: {
        ...currentState.todos,
        items: typedPersistedState.todos?.items || [],
        completed: typedPersistedState.todos?.completed || [],
      },
    };
  },
};

// Create the store with persistence middleware
export const useBoundStore = create<BoundStore>()(persist((...a) => createBoundStore(...a), persistConfig));

export const initialStore = useBoundStore.getState();
